import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import CategoryTabs from '@/pages/home/components/CategoryTabs';
import { movies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredMovies = useMemo(() => {
    let result = slug === 'all' ? [...movies] : movies.filter((m) => m.categorySlug === slug);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }

    return result;
  }, [slug, sortBy]);

  const categoryName = currentCategory?.name || '全部影片';
  const pageTitle = `${categoryName} - 91影视看片下载 | 天美麻花星空影视免费观看电视`;
  const pageDescription = `91影视看片下载${categoryName}频道，提供最新${categoryName}在线观看。天美麻花星空影视免费观看电视，高清不缓冲，免费追剧平台每日更新。`;

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: pageTitle,
            description: pageDescription,
            url: `https://www.91yingshi.com/category/${slug}`,
            inLanguage: 'zh-CN',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com/' },
                { '@type': 'ListItem', position: 2, name: categoryName },
              ],
            },
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="w-full px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
                {categoryName}
              </h1>
              <p className="text-foreground-400 text-sm mt-1">
                共 {filteredMovies.length} 部影片
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(['latest', 'rating', 'popular'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSortBy(s)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    sortBy === s
                      ? 'bg-primary-500 text-foreground-50'
                      : 'bg-background-100 text-foreground-300 hover:text-foreground-100 border border-background-200/50'
                  }`}
                >
                  {s === 'latest' ? '最新' : s === 'rating' ? '评分最高' : '最受欢迎'}
                </button>
              ))}
            </div>
          </div>

          <CategoryTabs activeSlug={slug} />

          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-movie-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-400 text-lg">该分类暂无影片</p>
              <Link
                to="/category/all"
                className="mt-4 inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary-500 text-foreground-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                浏览全部影片
              </Link>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}